"use client"

import Link from "next/link"
import Image from "next/image"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import LinkedinIcon from "../icons/LinkedinIcon"
import TwitterIcon from "../icons/TwitterIcon"
import YoutubeIcon from "../icons/YoutubeIcon"
import MetaIcon from "../icons/MetaIcon"
import { businessMenuItems, manageMenuItems } from "../navbar/components/menu-items"
import { useAppStore } from "@/stores/useAppStore"
import { languages } from "@/constants/languages"
import "../public-v33.css"

export default function FooterPublic() {
  const { language, setLanguage } = useAppStore();
  const handleLanguageChange = (value: string) => {
    setLanguage(value);
  };
  return (
    <footer className="v33-footer w-full">
      <div className="container px-4 py-14 xl:py-20 mx-auto">
        {/* Desktop Layout */}
        <div className="hidden xl:grid xl:grid-cols-5 xl:gap-16">
          {/* Start a Business */}
          <div className="col-span-1">
            <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Start a Business</h3>
            <div className="flex flex-col space-y-3">
              {businessMenuItems.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="v33-footer-link"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Manage a Business */}
          <div className="col-span-1">
            <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Manage a Business</h3>
            <div className="flex flex-col space-y-3">
              {manageMenuItems.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="v33-footer-link"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Protect a Business */}
          <div className="col-span-1">
            <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Protect a Business</h3>
            <div className="flex flex-col space-y-3">
              <Link
                href={"https://ipostal1.com/?ref=7111"}
                className="v33-footer-link"
              >
                Virtual Business Address
              </Link>
            </div>
          </div>

          {/* Company & Resources */}
          <div className="col-span-1">
            <div className="space-y-6">
              <div>
                <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Company</h3>
                <div className="flex flex-col space-y-3">
                  <Link href="/about.html" className="v33-footer-link">
                    About Us
                  </Link>
                  <Link href="/pricing.html" className="v33-footer-link">
                    Pricing
                  </Link>
                  <Link href="/business-os.html" className="v33-footer-link">
                    Platform
                  </Link>
                </div>
              </div>
              <div>
                <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Resources</h3>
                <div className="flex flex-col space-y-3">
                  <Link href="/blog" className="v33-footer-link">
                    Blog
                  </Link>
                  <Link href="/new-business-guide.html" className="v33-footer-link">
                    New Business Guide
                  </Link>
                  <Link href="/faq" className="v33-footer-link">
                    FAQs
                  </Link>
                  <Link href="/boi-reporting.html" className="v33-footer-link">
                    Do I need to file a BOI Report?
                  </Link>
                  <Link href="/start-filing.html" className="v33-footer-link">
                    Find a Professional
                  </Link>
                  <Link href="/affiliate-program.html" className="v33-footer-link">
                    Become a Partner
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="col-span-1">
            <h3 className="v33-sans font-semibold text-[15px] text-[#101c46] mb-4">Support</h3>
            <div className="flex flex-col space-y-3">
              <Link href="/help.html" className="v33-footer-link">
                Help Center
              </Link>
              <Link href="/tracking" className="v33-footer-link">
                Track Order
              </Link>
            </div>
          </div>
        </div>

        {/* Social Media, Logo and Language - Below Links */}
        <div className="hidden xl:flex xl:gap-8 xl:items-center xl:justify-between xl:mt-12 xl:pt-8 xl:border-t xl:border-gray-300">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Image
              src={"/file-business-textless-logo.webp"}
              alt="File Business Logo"
              width={48}
              height={48}
              className="h-12 w-12"
            />
            <div className="text-navy-800 font-semibold text-base">
              <div>File</div>
              <div>Business</div>
            </div>
          </div>

          {/* Social Media */}
          <div className="flex gap-4">
            <Link href="https://www.linkedin.com/company/filebusiness" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="LinkedIn">
              <LinkedinIcon className="h-5 w-5" />
            </Link>
            <Link href="https://x.com/filebizz" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="X">
              <TwitterIcon className="h-5 w-5" />
            </Link>
            <Link href="https://youtube.com/@filebusiness" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="YouTube">
              <YoutubeIcon className="h-5 w-5" />
            </Link>
            <Link href="https://www.facebook.com/people/File-Business/61581804187213/" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="Meta">
              <MetaIcon className="h-5 w-5" />
            </Link>
          </div>

          {/* Language Selector */}
          <Select
            value={language}
            onValueChange={handleLanguageChange}
          >
            <SelectTrigger className="w-[180px] border-gray-200">
              <div className="flex items-center w-full justify-between ">
                <div className="flex items-center gap-2.5">
                  {languages.find((lang) => lang.value === language)?.label}
                </div>
                <Image
                  src={`/assets/languageFlags/${language}.svg`}
                  alt={language}
                  width={18}
                  height={18}
                  className="rounded-full"
                />
              </div>
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.value} value={lang.value}>
                  <div className="flex items-center gap-2">
                    <Image
                      src={`/assets/languageFlags/${lang.value}.svg`}
                      alt={lang.label}
                      width={18}
                      height={18}
                      className="rounded-full"
                    />
                    <span>{lang.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mobile Layout */}
        <div className="flex flex-col xl:hidden items-center w-full">
          {/* Logo and name */}
          <div className="flex flex-col items-center gap-2 mt-2 mb-4">
            <Image src={"/file-business-textless-logo.webp"} alt="File Business Logo" width={48} height={48} className="h-12 w-12" />
            <div className="text-navy-800 font-bold text-lg text-center">
              <div>File Business</div>
            </div>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {/* Start a Business */}
            <AccordionItem value="start">
              <AccordionTrigger className="text-lg font-bold py-4">Start a Business</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  {businessMenuItems.map(link => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="v33-footer-link"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Manage a Business */}
            <AccordionItem value="manage">
              <AccordionTrigger className="text-lg font-semibold py-4">Manage a Business</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  {manageMenuItems.map(link => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="v33-footer-link"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Protect a Business */}
            <AccordionItem value="protect">
              <AccordionTrigger className="text-lg font-semibold py-4">Protect a Business</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  <Link
                    href={"https://ipostal1.com/?ref=7111"}
                    className="v33-footer-link"
                  >
                    Virtual Business Address
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="company">
              <AccordionTrigger className="text-lg font-semibold py-4">Company</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  <Link href="/about.html" className="v33-footer-link">
                    About Us
                  </Link>
                  <Link href="/pricing.html" className="v33-footer-link">
                    Pricing
                  </Link>
                  <Link href="/business-os.html" className="v33-footer-link">
                    Platform
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="resources">
              <AccordionTrigger className="text-lg font-semibold py-4">Resources</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  <Link href="/blog" className="v33-footer-link">
                    Blog
                  </Link>
                  <Link href="/new-business-guide.html" className="v33-footer-link">
                    New Business Guide
                  </Link>
                  <Link href="/faq" className="v33-footer-link">
                    FAQs
                  </Link>
                  <Link href="/boi-reporting.html" className="v33-footer-link">
                    Do I need to file a BOI Report?
                  </Link>
                  <Link href="/start-filing.html" className="v33-footer-link">
                    Find a Professional
                  </Link>
                  <Link href="/affiliate-program.html" className="v33-footer-link">
                    Become a Partner
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="support">
              <AccordionTrigger className="text-lg font-semibold py-4">Support</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col space-y-3 py-2">
                  <Link href="/help.html" className="v33-footer-link">
                    Help Center
                  </Link>
                  <Link href="/tracking" className="v33-footer-link">
                    Track Order
                  </Link>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Social Media, Logo and Language - Below Accordion */}
          <div className="w-full mt-8 pt-8 border-t border-gray-300">
            {/* Logo */}
            <div className="flex flex-col items-center gap-2 mb-6">
              <Image src={"/file-business-textless-logo.webp"} alt="File Business Logo" width={48} height={48} className="h-12 w-12" />
              <div className="text-navy-800 font-semibold text-base text-center">
                <div>File</div>
                <div>Business</div>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex justify-center gap-4 mb-6">
              <Link href="https://www.linkedin.com/company/filebusiness" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="LinkedIn">
                <LinkedinIcon className="h-5 w-5" />
              </Link>
              <Link href="https://x.com/filebizz" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="X">
                <TwitterIcon className="h-5 w-5" />
              </Link>
              <Link href="https://youtube.com/@filebusiness" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="YouTube">
                <YoutubeIcon className="h-5 w-5" />
              </Link>
              <Link href="https://www.facebook.com/people/File-Business/61581804187213/" className="rounded-full bg-white p-3 hover:bg-gray-200 shadow-md" aria-label="Meta">
                <MetaIcon className="h-5 w-5" />
              </Link>
            </div>

            {/* Language Selector */}
            <div className="flex justify-center">
              <Select
                value={language}
                onValueChange={handleLanguageChange}
              >
                <SelectTrigger className="w-[180px] border-gray-200">
                  <div className="flex items-center w-full justify-between ">
                    <div className="flex items-center gap-2.5">
                      {languages.find((lang) => lang.value === language)?.label}
                    </div>
                    <Image
                      src={`/assets/languageFlags/${language}.svg`}
                      alt={language}
                      width={18}
                      height={18}
                      className="rounded-full"
                    />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      <div className="flex items-center gap-2">
                        <Image
                          src={`/assets/languageFlags/${lang.value}.svg`}
                          alt={lang.label}
                          width={18}
                          height={18}
                          className="rounded-full"
                        />
                        <span>{lang.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Bottom Footer */}
      <div className="container px-4 py-6 mx-auto">
        <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center">
          {/* Policy links: top on mobile, right on desktop */}
          <div className="order-1 xl:order-2 flex flex-wrap justify-center xl:justify-end gap-4 text-sm">
            <Link href="/privacy.html" className="text-gray-500 hover:text-gray-900">Privacy Policy</Link>
            <Link href="/terms-of-service.html" className="text-gray-500 hover:text-gray-900">Terms of Service</Link>
            <Link href="/legal/subscription-terms" className="text-gray-500 hover:text-gray-900">Subscription Terms</Link>
            <Link href="/disclaimer.html" className="text-gray-500 hover:text-gray-900">Disclaimer Policy</Link>
            <Link href="/refunds.html" className="text-gray-500 hover:text-gray-900">Refund Policy</Link>
          </div>
          {/* Copyright: bottom on mobile, left on desktop */}
          <div className="order-2 xl:order-1 text-center xl:text-left text-sm text-gray-500 my-4 xl:my-0">
            Copyright © 2025 File Business
          </div>
        </div>
      </div>
      <div className="container px-4 py-6 mx-auto">
        <p className="text-sm text-gray-500">
          File.Business is a private document filing and compliance service and is not a government agency, law firm, tax advisory, or financial advisory firm. Use of our services does not create an attorney-client relationship. We do not provide legal, tax, or financial advice or review submissions for legal accuracy. Businesses are not required to use our services and may file directly with the appropriate government agency. Use of this website and services is governed by our <Link href={"/terms-of-service.html"} className="text-fb-primary">Terms of Service</Link> and <Link href={"/privacy.html"} className="text-fb-primary">Privacy Policy</Link>.
        </p>
      </div>
    </footer>
  )
}